import { FC } from "react";
import { Widget } from "@components-common/widgets";
import {
  getImage,
  getName,
  hasValidImageExtension,
} from "@components-common/tables";
import { Initials } from "taltech-styleguide";

type Props = {
  widgetProps: {
    id: string;
    title: string;
    subTitle?: string;
    className?: string;
  };
  data: any;
  visibleFields?: string[];
  height?: string;
};

/**
 * Component: InformationWidget
 *
 * A widget component that displays information based on the provided data and visible fields.
 *
 * Props:
 * @param {object} data - The data object containing information to display.
 * @param {string[]} [visibleFields=[]] - An array of field names to display. If empty, all fields in the data object are displayed.
 * @param {string} [height="auto"] - The height of the widget.
 * @param {object} [widgetProps] - Additional props to pass to the Widget component.
 *
 * @returns {JSX.Element} The rendered InformationWidget component.
 */
export const InformationWidget: FC<Props> = ({
  data,
  visibleFields = [],
  height = "auto",
  widgetProps,
}) => (
  <Widget {...widgetProps}>
    <div style={{ height: height }}>
      {visibleFields.length > 0
        ? visibleFields.map((field: string) => (
            <HeaderWithInfo
              key={field}
              data={data}
              field={field}
              value={data[field]}
            />
          ))
        : Object.entries(data).map(
            ([key, value]) =>
              typeof value === "string" && (
                <HeaderWithInfo
                  key={key}
                  data={data}
                  field={key}
                  value={value}
                />
              )
          )}
    </div>
  </Widget>
);

const imgPropsName = ["picture", "pictureUrl"];
const datePropsName = ["startDate", "endDate", "createdDate", "modifiedDate"];

/**
 * Component: HeaderWithInfo
 *
 * A component that renders a header and its associated value.
 *
 * Props:
 * @param {string} field - The field name.
 * @param {any} value - The value associated with the field.
 *
 * @returns {JSX.Element} The rendered HeaderWithInfo component.
 */
const HeaderWithInfo = ({ data, field, value }: any) => {
  const renderContent = () => {
    if (imgPropsName.includes(field)) {
      const imgUrl = getImage(data);
      const name = getName(data);

      if (hasValidImageExtension(imgUrl)) {
        return (
          <Initials className={"m-1"} imageUrl={imgUrl} name={name} size="lg" />
        );
      } else {
        return <Initials className={"m-1"} name={name} size="lg" />;
      }
    } else if (datePropsName.includes(field)) {
      return <p>{new Date(value).toLocaleDateString('en-GB')}</p>;
    } else {
      return <p>{value}</p>;
    }
  };
  return (
    <div key={field}>
      <h6 className="mb-0">{field.replace(/([a-z])([A-Z])/g, "$1 $2")}</h6>
      {renderContent()}
    </div>
  );
};
